import * as xo from "@attraqt/xo-js"
import { fhAnalyticsActions, fhAnalyticsUserActions } from "../constants/constants"
import { runWithTimeout } from "../utils/common-utils"
import { getEnvSpecificKeyByBrandAndLocale } from "../utils/configuration-utils"
import { getFHLocaleFormat } from "../utils/locale-utils"

type FhActionType = {
  action: string
  opposite?: boolean
  target: {
    [name: string]: string
  }
  sourceId?: string
  metadata?: {
    [name: string]: string | number | boolean
    price?: number
    quantity?: number
    currency?: string
    orderId?: string
    market?: string
  }
  segments?: string[]
  timestamp?: string
}

export const initFhAnalytics = () => {
  try {
    const key = getEnvSpecificKeyByBrandAndLocale(process.env.NEXT_PUBLIC_FREDHOPPER_CONFIG)

    xo.init({
      activity: {
        trackerKey: key,
      },
    })
  } catch (error) {
    console.log(`FREDHOPPER ANALYTICS ERROR: "${error}"`)
  }
}

initFhAnalytics()

const trackSingleFacet = (analyticsOptions) => {
  return xo.activity.send(analyticsOptions)
}

const sendFhAnalytics = ({
  action,
  locale,
  query = undefined,
  sourceId = undefined,
  itemId = undefined,
  shouldBeInterrupted = undefined,
  facets = undefined,
  facetId = undefined,
  path = undefined /* Pass it when tracking from the server */,
}) => {
  try {
    let newlySelectedFacets
    const searchTerm = query?.q
    const analyticsOptions: FhActionType = {
      action: fhAnalyticsActions.VIEW,
      target: {},
      metadata: {
        locale: getFHLocaleFormat(locale),
      },
    }
    if (sourceId) analyticsOptions.sourceId = sourceId

    switch (action) {
      case fhAnalyticsUserActions.PAGEVIEW:
      case fhAnalyticsUserActions.PARTIALS_PAGEVIEW:
        analyticsOptions.target.page = path ?? window?.location?.pathname
        if (searchTerm) {
          analyticsOptions.target["search-terms"] = String(searchTerm)
        }
        break

      case fhAnalyticsUserActions.PROCLICK:
      case fhAnalyticsUserActions.SPTCLICK:
      case fhAnalyticsUserActions.ARTCLICK:
      case fhAnalyticsUserActions.SUGG_PROCLICK:
      case fhAnalyticsUserActions.SUGG_SPTCLICK:
      case fhAnalyticsUserActions.SUGG_ARTCLICK:
        analyticsOptions.action = fhAnalyticsActions.CLICK
        analyticsOptions.target.product = `p_${itemId.toLowerCase()}`
        break

      case fhAnalyticsUserActions.FACETCLICK:
        analyticsOptions.action = fhAnalyticsActions.CLICK
        //filter the newly selected facets and send a tracking event for each
        newlySelectedFacets = Object.keys(facets.current).filter((fct) => facets.current[fct] && !facets.prev[fct])
        return newlySelectedFacets.forEach((fct) =>
          trackSingleFacet({ ...analyticsOptions, ...{ target: { facet: facetId, [facetId]: fct } } })
        )
    }

    //for products or spt clicks the url is changed
    //and analytics call is cancelled
    //so we wait for the analytics call to be resolved / rejected (with finally)
    //but we don't want to wait too much for it to become noticeable
    if (shouldBeInterrupted) {
      const fhAnalyticsTimeoutMsg = `
            Response from Fredhopper analytics function takes too long to block the user interface for.
            The user will be redirected to the selected page and this user action will not be logged in Fredhopper analytics.
          `.trim()
      return runWithTimeout(() => xo.activity.send(analyticsOptions), fhAnalyticsTimeoutMsg, 100)
    }
    return xo.activity.send(analyticsOptions)
  } catch (e) {
    console.log(`FREDHOPPER ANALYTICS ERROR: "${e}"`)
  }
}

export default sendFhAnalytics
